import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { Sgke081aResponseDepLstInfo } from 'sg-api';

@Component({
  selector: 'app-sgke080d-result',
  templateUrl: './sgke080d-result.component.html',
  styleUrl: './sgke080d-result.component.scss'
})
export class Sgke080dResultComponent implements OnInit, OnDestroy {
  /** 表データ */
  @Input() responseList: Array<Sgke081aResponseDepLstInfo>;
  /** 行ボタン押下Emitter */
  @Output() onDblClickRowEmitter: EventEmitter<Sgke081aResponseDepLstInfo> = new EventEmitter();
  /** 登録ボタン押下Emitter */
  @Output() onClickRegistEmitter = new EventEmitter();
  /** 取消ボタン押下Emitter */
  @Output() onClickCancelEmitter = new EventEmitter();

  /** ソート */
  @ViewChild('sort1', { static: true }) sort: MatSort;

  /** データソース */
  dataSource: MatTableDataSource<Sgke081aResponseDepLstInfo>;
  /** 選択した行の情報 */
  selection: SelectionModel<Sgke081aResponseDepLstInfo>;
  /** テーブル表示カラム */
  displayedColumnsCol: string[] = [
    'select',
    'payCtrNo',
    'visitName',
    'receivemoneydate',
    'kinkoAmt',
    'rcvmnyKeitai',
    'rcvmnyFclt',
    'kinkoOccurrenceYearmonthdate',
    'kinkoSts',
    'kinkoReason',
    'funoReason'
  ];

  /**
   * コンストラクタ
   * @memberof Sgke080dResultComponent
   */
  constructor(
  ) {
  }

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();

  /**
   * 初期処理
   */
  ngOnInit(): void {
    // 表データと、ソート/ページングを連携
    this.dataSource = new MatTableDataSource<Sgke081aResponseDepLstInfo>(this.responseList);
    this.selection = new SelectionModel<Sgke081aResponseDepLstInfo>(true, []);
  }

  /**
   * ViewInit後の呼び出し
   */
  ngAfterViewInit(): void {
    this.dataSource.sort ??= this.sort;
  }

  /**
   * 最終処理
   */
  ngOnDestroy(): void {
    //購読を停止する処理
    this.onDestroy$.next();
  }

  /**
   * テキストエリアの無効判定
   *
   * @param {Sgke081aResponseDepLstInfo} row 対象行情報
   * @return {boolean} true:無効, false:有効
   * @memberof Sgke080dResultComponent
   */
  isTexareaDisabled(row: Sgke081aResponseDepLstInfo): boolean {
    return row.kinkoStatuscode == '08';
  }

  /**
   * テキストエリアのバリデーションNG判定
   *
   * @param {Sgke081aResponseDepLstInfo} row 対象行情報
   * @return {boolean} true:バリデーションNG, false:バリデーションOK
   * @memberof Sgke080dResultComponent
   */
  isTextAreaInvalid(row: Sgke081aResponseDepLstInfo): boolean {
    // 全角文字１文字以上の正規表現
    const regex = /^[^\u0020-\u007E\uFF61-\uFF9F\r\n]+$/;

    // 選択行
    let invalid: boolean = this.selection.isSelected(row);
    // 有効
    invalid &&= !this.isTexareaDisabled(row);
    // 全角文字１文字以上でない
    invalid &&= !regex.test(row.funoReason);
    return invalid;
  }

  /**
   * テキストエリアのスタイル取得
   *
   * @param {Sgke081aResponseDepLstInfo} row 対象行情報
   * @return {string} スタイルに設定する文字列
   * @memberof Sgke080dResultComponent
   */
  getTextareaStyle(row: Sgke081aResponseDepLstInfo): string {
    // 下線の色
    let border: string = this.isTextAreaInvalid(row) ? 'red' : 'black';
    // フォーカス時のアウトライン色
    let outline: string = this.isTextAreaInvalid(row) ? 'red' : '#43a047';
    return `border-bottom-color: ${border};outline-color:${outline};`;
  }

  /**
   * 対象のチェックボックス変更時処理
   *
   * @param {Sgke081aResponseDepLstInfo} row 対象行情報
   * @memberof Sgke080dResultComponent
   */
  selectionToggle(row: Sgke081aResponseDepLstInfo) {
    this.selection.toggle(row);
  }

  /**
   * 不能理由変更時処理
   *
   * @param {Sgke081aResponseDepLstInfo} row 対象行情報
   * @param {string} value テキストエリアに入力した文字列
   * @memberof Sgke080dResultComponent
   */
  onChangeReason(row: Sgke081aResponseDepLstInfo, value: string): void {
    if (value?.length > 0) {
      this.selection.select(row);
    } else {
      this.selection.deselect(row);
    }
    row.funoReason = value;
  }

  /**
   * 行押下を親コンポーネントへ通知する処理
   *
   * @param {Sgke081aResponseDepLstInfo} row 対象行情報
   * @param {MouseEvent} event マウスイベント
   * @memberof Sgke080dResultComponent
   */
  onDblClickRow(row: Sgke081aResponseDepLstInfo, event?: MouseEvent): void {
    this.onDblClickRowEmitter.emit(row);
    event?.stopPropagation();
  }

  /**
   * 登録ボタン押下を親コンポーネントへ通知する処理
   *
   * @memberof Sgke080dResultComponent
   */
  onClickRegist(): void {
    this.onClickRegistEmitter.emit(this.selection.selected);
  }

  /**
   * 取消ボタン押下を親コンポーネントへ通知する処理
   *
   * @memberof Sgke080dResultComponent
   */
  onClickCancel(): void {
    this.onClickCancelEmitter.emit(this.selection.selected);
  }

  /**
   * 登録ボタン活性非活性判定
   *
   * @returns {boolean} true:非活性、false:活性
   * @memberof Sgke080dResultComponent
   */
  isRegistButtonInvalid(): boolean {
    const list = this.selection.selected;
    // 選択行で1つでもテキストエリアの無効またはinvalidがある場合は非活性
    return list.length == 0 || list.some(row => {
      return this.isTexareaDisabled(row) || this.isTextAreaInvalid(row);
    });
  }

  /**
   * 取消ボタン活性非活性判定
   *
   * @returns {boolean} true:非活性、false:活性
   * @memberof Sgke080dResultComponent
   */
  isDeleteButtonInvalid(): boolean {
    const list = this.selection.selected;
    // 選択行で一つでもテキストエリア有効があれば非活性
    return list.length == 0 || list.some(row => {
      return !this.isTexareaDisabled(row);
    });
  }
}